import axios from 'axios'
import type { Router } from 'vue-router'
import { useAuthUserStore } from './user/authUserStore'
import { storeToRefs } from 'pinia'
import * as Sentry from '@sentry/vue'

export const ERROR_TYPES = {
    UNAUTHORISED: 'unauthorised',
    FORBIDDEN: 'forbidden',
    CONFLICT: 'conflict',
    VALIDATION: 'validation',
    THROTTLED: 'throttled',
} as const

type UnauthorisedError = {
    type: typeof ERROR_TYPES.UNAUTHORISED
    message: string
    handledNavigation?: boolean
}

type ForbiddenError = {
    type: typeof ERROR_TYPES.FORBIDDEN
    message: string
    handledNavigation?: boolean
}

type ConflictError = {
    type: typeof ERROR_TYPES.CONFLICT
    message: string
    conflicts: Record<string, string[]>
    handledNavigation?: boolean
}

type ValidationError = {
    type: typeof ERROR_TYPES.VALIDATION
    message: string
    errors: Record<string, string[]>
    handledNavigation?: boolean
}

type ThrottledError = {
    type: typeof ERROR_TYPES.THROTTLED
    message: string
    handledNavigation?: boolean
}

export type ApiError =
    | ConflictError
    | ValidationError
    | ThrottledError

export function isApiError(error: unknown): error is ApiError {
    return (
        typeof error === 'object' &&
        error !== null &&
        'type' in error &&
        'message' in error
    )
}

export function isThrottledError(error: unknown): error is ThrottledError {
    return isApiError(error) && error.type === ERROR_TYPES.THROTTLED
}

const axiosInstance = axios.create({
    baseURL: import.meta.env.VITE_APP_API_URL as string,
    withCredentials: true,
    withXSRFToken: true,
    headers: {
        Accept: 'application/json',
        'X-CliniShift-Api-Version': '2024-08-01',
    },
})

export const setupInterceptors = (router: Router) => {
    const authStore = useAuthUserStore()
    const { hasCheckedSession } = storeToRefs(authStore)

    axiosInstance.interceptors.response.use(
        (response) => response,
        (err) => {
            let error = err

            if (typeof error.response !== 'undefined') {
                const status = error.response.status

                if (status === 401 || status === 419) {
                    // If we've checked the state then it's safe to use any 401 or similar error as a reason to redirect/logout
                    if (hasCheckedSession.value) {
                        // TODO: Clean up how we handle resetting auth user
                        const authUserStore = useAuthUserStore()
                        const { user: authUser } = storeToRefs(authUserStore)
                        authUser.value = null

                        router.push({ name: '/(guest)/login' })

                        error = { type: ERROR_TYPES.UNAUTHORISED, message: 'Your password has expired. Please update your password.', handledNavigation: true } as UnauthorisedError
                    }
                }

                if (status === 403) {
                    if (error.response.data.password_expired === true) {
                        router.push({ name: '/(app)/user/settings', hash: '#password-expired' })

                        error = { type: ERROR_TYPES.FORBIDDEN, message: 'Your password has expired. Please update your password.', handledNavigation: true } as ForbiddenError
                    } else {
                        error = { type: ERROR_TYPES.FORBIDDEN, message: 'You do not have permission to perform this action.' } as ForbiddenError
                    }
                }

                if (status === 404) {
                    error = { message: 'Not found.' } as ApiError
                }

                if (status === 409) {
                    error = { type: ERROR_TYPES.CONFLICT, message: 'Conflict. The requested resource is in a conflicting state.', conflicts: {} } as ConflictError
                }

                if (status === 422) {
                    error = error.response.data as ValidationError
                }

                if (status === 429) {
                    error = { type: ERROR_TYPES.THROTTLED, message: 'Too many requests. Please try again later.' } as ThrottledError
                }

                if (status === 500) {
                    Sentry.captureException(err)

                    error = { message: 'An unexpected error occurred. Please try again later.' } as ApiError
                }
            }

            return Promise.reject(error)
        })
}

export default axiosInstance
